import { useLayoutEffect } from "react";

const revealSelector = "[data-scroll-reveal]";

export function useScrollReveal(enabled: boolean) {
  useLayoutEffect(() => {
    if (!enabled) return;

    const root = document.documentElement;
    const elements = Array.from(
      document.querySelectorAll<HTMLElement>(revealSelector),
    );
    const reducedMotion = window.matchMedia(
      "(prefers-reduced-motion: reduce)",
    );

    if (
      !elements.length ||
      reducedMotion.matches ||
      !("IntersectionObserver" in window)
    ) {
      return;
    }

    root.classList.add("scroll-reveal-ready");
    let revealFrame = 0;

    const observer = new IntersectionObserver(
      (entries) => {
        const visible = entries.filter((entry) => entry.isIntersecting);
        if (!visible.length) return;

        revealFrame = window.requestAnimationFrame(() => {
          visible.forEach((entry) => {
            entry.target.classList.add("is-scroll-revealed");
            observer.unobserve(entry.target);
          });
        });
      },
      {
        threshold: 0.12,
        rootMargin: "0px 0px -8% 0px",
      },
    );

    const activationFrame = window.requestAnimationFrame(() => {
      root.classList.add("scroll-reveal-active");
      elements.forEach((element) => observer.observe(element));
    });

    return () => {
      window.cancelAnimationFrame(activationFrame);
      window.cancelAnimationFrame(revealFrame);
      observer.disconnect();
      root.classList.remove("scroll-reveal-active");
      root.classList.remove("scroll-reveal-ready");
      elements.forEach((element) =>
        element.classList.remove("is-scroll-revealed"),
      );
    };
  }, [enabled]);
}
